import { loadGLightbox } from "common/glightbox-loader";
import { remoteImageStaticUrl } from "common/utils/remote-image";

import "./jiffymade-landing-gallery.scss";

const $getImages = () => document.querySelectorAll(".js-jiffymade-landing-jiffy-image");

const loadImage = src =>
  new Promise(resolve => {
    const image = new Image();
    image.src = src;
    image.onload = () => resolve();
  });

const SWAP_IMAGE_INTERVAL = 3000;
const FADE_IN_CLASS = "jiffymade-landing-gallery__image--fade-in";
const FADE_OUT_CLASS = "jiffymade-landing-gallery__image--fade-out";

const IMAGE_PATHS = [
  "gen/jiffymade-landing/create-jiffy/create-with-jiffy-1.png",
  "gen/jiffymade-landing/create-jiffy/create-with-jiffy-2.png",
  "gen/jiffymade-landing/create-jiffy/create-with-jiffy-3.jpeg",
  "gen/jiffymade-landing/create-jiffy/create-with-jiffy-4.png",
  "gen/jiffymade-landing/create-jiffy/create-with-jiffy-5.jpeg",
  "gen/jiffymade-landing/create-jiffy/create-with-jiffy-6.jpeg",
  "gen/jiffymade-landing/reviews/2.png",
  "gen/jiffymade-landing/reviews/3.png",
  "gen/jiffymade-landing/reviews/4.png",
  "gen/jiffymade-landing/reviews/5.png",
  "gen/jiffymade-landing/reviews/6.png",
  "gen/jiffytransfers-landing-v2/made-with-jiffy/made-with-jiffy-7.png",
  "gen/jiffytransfers-landing-v2/made-with-jiffy/made-with-jiffy-8.png",
  "gen/jiffytransfers-landing-v2/made-with-jiffy/made-with-jiffy-9.png"
];

const FULL_IMAGE_OPTIONS = { auto: "format" };
const THUMBNAIL_OPTIONS = { auto: "format", h: 800, dpr: 1.5, q: 80 };

const ALL_IMAGES = IMAGE_PATHS.map(path => remoteImageStaticUrl(path));

const extractPathFromUrl = url => {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname.startsWith("/") ? urlObj.pathname.slice(1) : urlObj.pathname;
  } catch {
    const host = window.jiffy?.remoteImageHost;
    if (host && url.includes(host)) {
      return url.replace(new RegExp(`^https?://${host.replace(/^https?:\/\//, "")}/`), "");
    }
    return url;
  }
};

let lightbox;
const currentImages = Array.from($getImages()).map($image => $image.src.split("?")[0]);
const otherImages = ALL_IMAGES.filter(image => !currentImages.includes(image));
const loadPromises = currentImages.map(image => {
  const path = extractPathFromUrl(image);
  return loadImage(remoteImageStaticUrl(path, THUMBNAIL_OPTIONS));
});

const showLightbox = index => {
  lightbox?.openAt(index);
};

const swapRandomImage = () => {
  const previousImageIndex = Math.floor(Math.random() * currentImages.length);
  const newImageIndex = Math.floor(Math.random() * otherImages.length);

  const newImage = otherImages[newImageIndex];
  const newImagePath = IMAGE_PATHS[ALL_IMAGES.indexOf(newImage)];
  const newImageSrc = remoteImageStaticUrl(newImagePath, THUMBNAIL_OPTIONS);

  if (currentImages.includes(newImage)) {
    return swapRandomImage();
  }

  otherImages[newImageIndex] = currentImages[previousImageIndex];
  currentImages[previousImageIndex] = newImage;

  const promise = loadPromises[newImage] || (loadPromises[newImage] = loadImage(newImageSrc));

  return promise.then(() => {
    const $images = $getImages();
    const $imageToSwap = $images[previousImageIndex];
    $imageToSwap.classList.remove(FADE_IN_CLASS);
    $imageToSwap.classList.add(FADE_OUT_CLASS);
    $imageToSwap.src = newImageSrc;
    return new Promise(resolve =>
      // eslint-disable-next-line no-promise-executor-return
      setTimeout(() => {
        $imageToSwap.classList.remove(FADE_OUT_CLASS);
        $imageToSwap.classList.add(FADE_IN_CLASS);
        resolve();
      }, 300)
    );
  });
};

const initGLightbox = () => {
  const initializeGLightbox = () => {
    lightbox = window?.GLightbox({
      elements: IMAGE_PATHS.map(path => ({
        href: remoteImageStaticUrl(path, FULL_IMAGE_OPTIONS),
        type: "image"
      })),
      touchNavigation: true,
      loop: false,
      autoplayVideos: false
    });
  };

  loadGLightbox(initializeGLightbox);
};

const initImageSwap = () => {
  Promise.all(loadPromises).then(() => {
    const swap = () => {
      setTimeout(async () => {
        await swapRandomImage();
        swap();
      }, SWAP_IMAGE_INTERVAL);
    };
    swap();
  });
};

export const initGallery = () => {
  initGLightbox();
  $getImages().forEach(($image, index) => {
    $image.addEventListener("click", () => {
      const currentImage = currentImages[index];
      const imageIndex = ALL_IMAGES.indexOf(currentImage);
      if (imageIndex !== -1) {
        showLightbox(imageIndex);
      }
    });
  });

  initImageSwap();
};

export default initGallery;
